import type { HotelSearchMapperInput, HotelSearchRequest, Occupancy } from '../api/hotels.types';

/** Splits `total` into `parts` as evenly as possible, extra units going to the first parts. */
function splitEvenly(total: number, parts: number): number[] {
  const base = Math.trunc(total / parts);
  const remainder = total - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < remainder ? 1 : 0));
}

/**
 * Distributes the search totals (adults, children and their ages) across the requested rooms.
 * The searcher counts guests for the whole stay, so 4 adults in 2 rooms means 2 + 2 — never 4
 * per room. Every room keeps at least one adult; children ages follow the children order.
 */
export function distributeOccupancy(input: {
  adults: number;
  children?: number;
  childrenAges?: number[];
  rooms?: number;
}): Occupancy[] {
  const adults = Math.max(1, Math.trunc(input.adults) || 1);
  const rooms = Math.min(adults, Math.max(1, Math.trunc(input.rooms ?? 1) || 1));
  const children = Math.max(0, Math.trunc(input.children ?? 0) || 0);
  const ages = input.childrenAges ?? [];
  const hasAllAges = ages.length === children;

  const adultsByRoom = splitEvenly(adults, rooms);
  const childrenByRoom = splitEvenly(children, rooms);

  let nextChild = 0;
  return adultsByRoom.map((roomAdults, i) => {
    const roomChildren = childrenByRoom[i];
    const occupancy: Occupancy = { adults: roomAdults, children: roomChildren };
    if (hasAllAges && children > 0) {
      occupancy.childrenAges = ages.slice(nextChild, nextChild + roomChildren);
    }
    nextChild += roomChildren;
    return occupancy;
  });
}

/**
 * Maps presentation-layer search values to the real API request body.
 * Requires a selected destination ID; throws if missing.
 */
export function mapHotelSearchRequest(input: HotelSearchMapperInput): HotelSearchRequest {
  if (!input.destinationId || input.destinationId.trim().length === 0) {
    throw new Error('mapHotelSearchRequest: destinationId is required');
  }

  if (!input.countryCode || input.countryCode.trim().length === 0) {
    throw new Error('mapHotelSearchRequest: countryCode is required');
  }

  return {
    destinationId: input.destinationId,
    countryCode: input.countryCode,
    checkIn: input.checkIn,
    checkOut: input.checkOut,
    occupancy: distributeOccupancy(input),
    stars: input.stars ?? [],
  };
}
